import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Popover from 'components/v2/Popover/Popover';
import { ChromePicker } from 'react-color';

export interface ColorInputFieldProps {
  fieldName: string;
  onColorSelected: (colorValue: string) => void;
  color?: string | null;
  defaultColor?: string;
  emptyLabel?: string;
  isEmpty?: boolean;
  isDisabled?: boolean;
  inModal?: boolean;
}

const ColorInputField: React.FC<ColorInputFieldProps> = ({
  fieldName,
  onColorSelected,
  color,
  defaultColor = '#FFFFFF',
  emptyLabel = 'Default',
  isEmpty = false,
  isDisabled = false,
  inModal = false,
}) => {
  const [sketchPickerColor, setSketchPickerColor] = useState<string>(
    color || defaultColor
  );
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const pointerStartedInPickerRef = useRef(false);

  const pickerColor = sketchPickerColor;
  const shouldShowEmptyState = isEmpty && !color;
  const currentColor = shouldShowEmptyState ? defaultColor : pickerColor;
  const popoverId = useMemo(
    () => `color-input-${fieldName.replace(/[^A-Za-z0-9_-]/g, '-')}`,
    [fieldName]
  );
  const outsideInteractionLayerStyle = useMemo<React.CSSProperties>(
    () => ({
      background: 'transparent',
      bottom: 0,
      cursor: 'default',
      left: 0,
      position: 'fixed',
      right: 0,
      top: 0,
      zIndex: inModal ? 1051 : 59,
    }),
    [inModal]
  );

  useEffect(() => {
    setSketchPickerColor(color || defaultColor);
  }, [color, defaultColor]);

  useEffect(() => {
    if (!isOpen) {
      pointerStartedInPickerRef.current = false;
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsidePicker = pickerRef.current?.contains(target);
      const clickedTrigger = document
        .getElementById(popoverId)
        ?.contains(target);

      if (clickedInsidePicker || clickedTrigger) {
        pointerStartedInPickerRef.current = false;
        return;
      }

      if (pointerStartedInPickerRef.current) {
        pointerStartedInPickerRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      setIsOpen(false);
    };

    const clearPointerStartedInPicker = () => {
      // Keep the flag long enough to catch the click generated after a drag.
      window.setTimeout(() => {
        pointerStartedInPickerRef.current = false;
      }, 100);
    };

    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('mouseup', clearPointerStartedInPicker, true);
    document.addEventListener('pointerup', clearPointerStartedInPicker, true);
    document.addEventListener('touchend', clearPointerStartedInPicker, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener(
        'mouseup',
        clearPointerStartedInPicker,
        true
      );
      document.removeEventListener(
        'pointerup',
        clearPointerStartedInPicker,
        true
      );
      document.removeEventListener(
        'touchend',
        clearPointerStartedInPicker,
        true
      );
    };
  }, [isOpen, popoverId]);

  const markPointerStartedInPicker = () => {
    pointerStartedInPickerRef.current = true;
  };

  const handleOutsideInteractionClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (pointerStartedInPickerRef.current) {
      pointerStartedInPickerRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    setIsOpen(false);
  };

  const outsideInteractionLayer =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            aria-hidden="true"
            className="color-picker__interaction-layer"
            onMouseDown={event => {
              event.preventDefault();
            }}
            onClick={handleOutsideInteractionClick}
            role="presentation"
            style={outsideInteractionLayerStyle}
          />,
          document.body
        )
      : null;

  return (
    <div className="color-picker">
      {outsideInteractionLayer}
      <Popover
        buttonClassName="btn color-picker__button"
        className="color-picker__popover-layer"
        closeOnOutsideClick={false}
        id={popoverId}
        inModal={inModal}
        isOpen={isOpen}
        isDisabled={isDisabled}
        popoverTriggerMethod="manual"
        setIsOpen={setIsOpen}
        trigger={
          <>
            <span className="color-picker__color">
              {shouldShowEmptyState ? emptyLabel : currentColor}
            </span>
            <div
              className="color-picker__color-view"
              style={{ backgroundColor: currentColor }}
            />
          </>
        }
      >
        <div
          ref={pickerRef}
          onMouseDown={markPointerStartedInPicker}
          onPointerDown={markPointerStartedInPicker}
          onTouchStart={markPointerStartedInPicker}
          role="presentation"
        >
          <ChromePicker
            color={pickerColor}
            onChange={selectedColor => {
              setSketchPickerColor(selectedColor.hex);
            }}
            onChangeComplete={selectedColor => {
              onColorSelected(selectedColor.hex);
            }}
            disableAlpha
          />
        </div>
      </Popover>
    </div>
  );
};

export default ColorInputField;
